import { useContext, useState, useEffect } from 'react';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { CompanyMatchQuery } from 'interfaceAdapter/queries/CompanyMatch';
import { ResponseMeetingRequestRepository } from 'interfaceAdapter/repositories/ResponseMeetingRequest';
import { IMatch, MATCH_STATUSES } from 'domain/entities/Match/Match';
import { useModal } from 'ui/components/screens/modals/useModal';
import { useForm } from 'react-hook-form';
import yup from 'utils/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLoading } from 'application/contexts/useLoading';
import { companyUserContext } from 'application/contexts/useCompanyUser';
import { PROFILE_STAGE } from 'domain/entities/CompanyUser/CompanyUser';

const schema = yup
    .object({
        reason: yup.string().required().max(1000),
        match_id: yup.number().required(),
    })
    .required();

type DeclineForm = yup.InferType<typeof schema>;

export const useMatchList = () => {
    const diContainer = useContext(diContainerContext);
    const companyMatchQuery = diContainer.resolve(CompanyMatchQuery);
    const meetingRequestRepository = diContainer.resolve(ResponseMeetingRequestRepository);
    const { loading, setLoading } = useLoading();
    const [matchList, setMatchList] = useState<IMatch[]>([]);
    const [offerList, setOfferList] = useState<IMatch[]>([]);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [showSnackbarMessage, setShowSnackbarMessage] = useState('');
    const declineModal = useModal();
    const { openModal, createModal, closeModal } = declineModal;
    const { user } = useContext(companyUserContext);
    const needShowModal = user?.account_profile_stage !== PROFILE_STAGE.COMPLETE;
    const needRegistrationModal = useModal();
    const { openModal: openNeedRegistrationModal, createModal: createNeedRegistrationModal } = needRegistrationModal;

    const {
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm<DeclineForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            reason: '',
        },
    });

    useEffect(() => {
        const fetchMatchList = async () => {
            setLoading(true);
            try {
                const matchList = await companyMatchQuery.fetchList();
                setMatchList(matchList.filter((match) => match.status === MATCH_STATUSES.MATCH_STATUS_CONNECTED));
                setOfferList(matchList.filter((match) => match.status === MATCH_STATUSES.MATCH_STATUS_WAITING_ACCEPT));
            } finally {
                setLoading(false);
            }
        };
        fetchMatchList();
    }, []);

    const accept = async (match_id: number) => {
        setLoading(true);
        try {
            await meetingRequestRepository.accepted(match_id);
            const matchList = await companyMatchQuery.fetchList();
            setMatchList(matchList.filter((match) => match.status === MATCH_STATUSES.MATCH_STATUS_CONNECTED));
            setOfferList(matchList.filter((match) => match.status === MATCH_STATUSES.MATCH_STATUS_WAITING_ACCEPT));
            setShowSnackbarMessage('オファーを承認しました');
            setShowSnackbar(true);
            setTimeout(() => {
                setShowSnackbar(false);
                setShowSnackbarMessage('');
            }, 2000);
        } finally {
            setLoading(false);
        }
    };

    const decline = handleSubmit(async ({ match_id, reason }) => {
        setLoading(true);
        try {
            await meetingRequestRepository.declined(match_id, reason);
            const matchList = await companyMatchQuery.fetchList();
            setMatchList(matchList.filter((match) => match.status === MATCH_STATUSES.MATCH_STATUS_CONNECTED));
            setOfferList(matchList.filter((match) => match.status === MATCH_STATUSES.MATCH_STATUS_WAITING_ACCEPT));
            setShowSnackbarMessage('オファーをお断りしました');
            setShowSnackbar(true);
            closeModal();
            setTimeout(() => {
                setShowSnackbar(false);
                setShowSnackbarMessage('');
            }, 2000);
        } finally {
            setLoading(false);
        }
    });

    return {
        loading,
        matchList,
        offerList,
        accept,
        decline,
        showSnackbarMessage,
        showSnackbar,
        setShowSnackbar,
        setValue,
        errors,
        declineModal,
        openModal,
        createModal,
        needShowModal,
        openNeedRegistrationModal,
        createNeedRegistrationModal,
    };
};
